import { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  Button,
  Space,
  Spin,
  Alert,
  Switch,
  Typography,
  Tooltip,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  StarFilled,
} from "@ant-design/icons";
import kpApi from "./keywordPointsData";
import { triggerNotification } from "../../common/notify";
import TagSelector from "../../common/TagSelector";

const tagsKey = (tags) => tags.map((t) => `${t.id}:${t.points}`).join(",");

const KeywordPointsDrawer = ({ open, blog, onClose, onToggleDefault, togglingDefaultId, onCountChange }) => {
  const blogId = blog?.id ?? null;

  const [selectedTags, setSelectedTags] = useState([]);
  const [savedKey, setSavedKey]         = useState("");
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [loadError, setLoadError]       = useState(null);

  const isDirty = tagsKey(selectedTags) !== savedKey;

  useEffect(() => {
    if (blogId != null) onCountChange?.(String(blogId), selectedTags.length);
  }, [blogId, selectedTags.length, onCountChange]);

  const fetchData = useCallback(async () => {
    if (!blogId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { items } = await kpApi.listKeywordPoints(blogId);
      const tags = items.map((r) => ({
        id: r.tagId ?? r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
      }));
      setSelectedTags(tags);
      setSavedKey(tagsKey(tags));
    } catch (err) {
      const mapped = kpApi.mapError(err);
      setLoadError(mapped);
      if (mapped.code !== "NOT_FOUND") {
        triggerNotification({
          type: "error",
          title: "Failed to load keyword points",
          message: mapped.message,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    if (!open) {
      setSelectedTags([]);
      setSavedKey("");
      setLoadError(null);
      return;
    }
    if (blogId) fetchData();
  }, [open, blogId, fetchData]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const items = selectedTags.map((t) => ({
        tag_id: Number(t.id),
        points: Number(t.points) || 0,
      }));
      await kpApi.bulkUpsertKeywordPoints(blogId, items);
      setSavedKey(tagsKey(selectedTags));
      await fetchData();
      triggerNotification({
        type: "success",
        title: `${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"} saved`,
      });
    } catch (err) {
      const mapped = kpApi.mapError(err);
      triggerNotification({ type: "error", title: "Save failed", message: mapped.message });
    } finally {
      setSaving(false);
    }
  };

  const headerSubtitle = blog
    ? `Blog #${blog.id}${blog.title ? ` — ${blog.title}` : ""}`
    : "";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      maskClosable={false}
      width={600}
      title="Manage keyword points"
      destroyOnClose
      extra={
        <Space>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              disabled={loading || saving}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={loading || !isDirty}
          >
            Save tags
          </Button>
        </Space>
      }
    >
      {headerSubtitle && (
        <Typography.Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
          {headerSubtitle}
        </Typography.Text>
      )}

      {blog && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Switch
              checked={Number(blog.isDefault) === 1}
              checkedChildren={<StarFilled />}
              loading={String(togglingDefaultId) === String(blog.id)}
              onChange={(checked) => onToggleDefault?.(blog, checked ? 1 : 0)}
            />
            <Typography.Text>Set as default blog</Typography.Text>
          </Space>
        </div>
      )}

      {loadError && loadError.code === "NOT_FOUND" ? (
        <Alert
          type="warning"
          showIcon
          message="Blog not found"
          description="This blog may have been deleted. Close the drawer and refresh the list."
        />
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            Select tags from the global tags library. Drag to reorder. Set points per tag.
          </Typography.Text>
          <TagSelector
            value={selectedTags}
            onChange={setSelectedTags}
            withPoints
            placeholder="Search and select tags…"
          />
          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {selectedTags.length} tag{selectedTags.length === 1 ? "" : "s"} selected
            </Typography.Text>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default KeywordPointsDrawer;
